package com.tovtalk.ui.components

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.LocalIndication
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.style.TextOverflow
import com.tovtalk.ui.theme.TovTalkDurations
import com.tovtalk.ui.components.TovTalkText
import com.tovtalk.ui.theme.TovTalkEasings
import com.tovtalk.ui.theme.TovTalkTheme

enum class TovTalkButtonVariant {
    Primary,
    Secondary,
    Ghost,
    Danger,
}

enum class TovTalkButtonSize {
    Small,
    Medium,
    Large,
}

enum class IconPosition {
    Start,
    End
}

@Composable
fun TovTalkButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    variant: TovTalkButtonVariant = TovTalkButtonVariant.Primary,
    size: TovTalkButtonSize = TovTalkButtonSize.Medium,
    enabled: Boolean = true,
    loading: Boolean = false,
    icon: ImageVector? = null,
    iconPosition: IconPosition = IconPosition.Start,
    fullWidth: Boolean = false,
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isPressed by interactionSource.collectIsPressedAsState()

    val scale by animateFloatAsState(
        targetValue = if (isPressed && enabled && !loading) 0.97f else 1f,
        animationSpec = tween(
            durationMillis = TovTalkDurations.fast,
            easing = TovTalkEasings.snappy
        ),
        label = "buttonScale"
    )

    val alpha = if (enabled && !loading) 1f else 0.5f

    val buttonHeight = when (size) {
        TovTalkButtonSize.Small -> TovTalkTheme.sizes.buttonHeightSm
        TovTalkButtonSize.Medium -> TovTalkTheme.sizes.buttonHeightMd
        TovTalkButtonSize.Large -> TovTalkTheme.sizes.buttonHeightLg
    }

    val horizontalPadding = when (size) {
        TovTalkButtonSize.Small -> TovTalkTheme.spacing.sm
        TovTalkButtonSize.Medium -> TovTalkTheme.spacing.md
        TovTalkButtonSize.Large -> TovTalkTheme.spacing.lg
    }

    val typography = when (size) {
        TovTalkButtonSize.Small -> TovTalkTheme.typography.labelMedium
        TovTalkButtonSize.Medium -> TovTalkTheme.typography.labelLarge
        TovTalkButtonSize.Large -> TovTalkTheme.typography.titleSmall
    }

    val iconSize = when (size) {
        TovTalkButtonSize.Small -> TovTalkTheme.sizes.iconSm
        TovTalkButtonSize.Medium -> TovTalkTheme.sizes.iconMd
        TovTalkButtonSize.Large -> TovTalkTheme.sizes.iconMd
    }

    Box(
        modifier = modifier
            .then(if (fullWidth) Modifier.fillMaxWidth() else Modifier)
            .scale(scale)
            .alpha(alpha)
            .height(buttonHeight)
            .clip(TovTalkTheme.shapes.button)
            .then(
                when (variant) {
                    TovTalkButtonVariant.Primary -> Modifier.background(
                        brush = Brush.horizontalGradient(
                            colors = listOf(
                                TovTalkTheme.colors.gradientStart,
                                TovTalkTheme.colors.gradientEnd
                            )
                        )
                    )
                    TovTalkButtonVariant.Secondary -> Modifier
                        .background(TovTalkTheme.colors.backgroundPrimary.copy(alpha = 0f))
                        .border(
                            width = TovTalkTheme.sizes.borderWidth,
                            color = TovTalkTheme.colors.brand,
                            shape = TovTalkTheme.shapes.button
                        )
                    TovTalkButtonVariant.Ghost -> Modifier.background(TovTalkTheme.colors.backgroundPrimary.copy(alpha = 0f))
                    TovTalkButtonVariant.Danger -> Modifier.background(TovTalkTheme.colors.error)
                }
            )
            .clickable(
                interactionSource = interactionSource,
                indication = LocalIndication.current,
                enabled = enabled && !loading,
                role = Role.Button,
                onClick = onClick
            )
            .padding(horizontal = horizontalPadding),
        contentAlignment = Alignment.Center
    ) {
        if (loading) {
            CircularProgressIndicator(
                modifier = Modifier.size(iconSize),
                color = when (variant) {
                    TovTalkButtonVariant.Primary, TovTalkButtonVariant.Danger -> TovTalkTheme.colors.onBrand
                    TovTalkButtonVariant.Secondary, TovTalkButtonVariant.Ghost -> TovTalkTheme.colors.brand
                },
                strokeWidth = TovTalkTheme.sizes.borderWidth
            )
        } else {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center
            ) {
                if (icon != null && iconPosition == IconPosition.Start) {
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        modifier = Modifier.size(iconSize),
                        tint = when (variant) {
                            TovTalkButtonVariant.Primary, TovTalkButtonVariant.Danger -> TovTalkTheme.colors.onBrand
                            TovTalkButtonVariant.Secondary, TovTalkButtonVariant.Ghost -> TovTalkTheme.colors.brand
                        }
                    )
                    Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xs))
                }

                TovTalkText(
                    text = text,
                    style = typography,
                    color = when (variant) {
                        TovTalkButtonVariant.Primary, TovTalkButtonVariant.Danger -> TovTalkTheme.colors.onBrand
                        TovTalkButtonVariant.Secondary, TovTalkButtonVariant.Ghost -> TovTalkTheme.colors.brand
                    },
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )

                if (icon != null && iconPosition == IconPosition.End) {
                    Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xs))
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        modifier = Modifier.size(iconSize),
                        tint = when (variant) {
                            TovTalkButtonVariant.Primary, TovTalkButtonVariant.Danger -> TovTalkTheme.colors.onBrand
                            TovTalkButtonVariant.Secondary, TovTalkButtonVariant.Ghost -> TovTalkTheme.colors.brand
                        }
                    )
                }
            }
        }
    }
}
